import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api } from "../api/client";
import { identifyUser, resetAnalytics } from "./analytics";
import type { CompleteLessonResult, WorldSnapshot } from "../shared/types";

type AuthState = "loading" | "signed_out" | "needs_onboarding" | "ready";

interface Store {
  auth: AuthState;
  email: string | null;
  /** The signed-in account's player id ("" until onboarded). Identity detail lives on snapshot.me. */
  playerId: string | null;
  isAdmin: boolean;
  snapshot: WorldSnapshot | null;
  /** Why the user is looking at the login screen (expired link, cookie not sticking, …). */
  authNotice: string | null;
  /** Email to prefill on the login form (arrived via an ?invite= link). */
  prefillEmail: string | null;
  /** Bumped to force-replay the welcome tour (e.g. the "Replay tour" button in Settings). */
  tourNonce: number;
  replayTour: () => void;
  refresh: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  signInWithCode: (email: string, code: string) => Promise<void>;
  /** Preview-only one-tap sign-in (the server route 404s in production). */
  previewSignIn: () => Promise<void>;
  createPlayer: (childName: string, character: string) => Promise<void>;
  renamePlayer: (childName: string) => Promise<void>;
  changeCharacter: (character: string) => Promise<void>;
  completeLesson: (nodeId: string, score: number) => Promise<CompleteLessonResult>;
  logout: () => Promise<void>;
}

const Ctx = createContext<Store | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>("loading");
  const [email, setEmail] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [snapshot, setSnapshot] = useState<WorldSnapshot | null>(null);
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  const [prefillEmail, setPrefillEmail] = useState<string | null>(null);
  const [tourNonce, setTourNonce] = useState(0);
  // When the last successful token/code exchange happened. A 401 from /api/auth/me right
  // after a 200 exchange means the session cookie didn't stick (seen when a stale service
  // worker intercepts /api/* on WebKit) — worth retrying and, if it persists, telling the
  // user something actionable instead of silently bouncing them back to the login screen.
  const lastExchangeAt = useRef(0);

  const refresh = useCallback(async () => {
    const snap = await api.getGarden();
    setSnapshot(snap);
  }, []);

  const refreshAuth = useCallback(async () => {
    // Only a real 401 means signed out. Anything else (network blip, the one-off 500 while
    // a deploy restarts the Worker/DO) is transient — retry briefly instead of bouncing a
    // validly-signed-in user to the login screen.
    for (let attempt = 0; ; attempt++) {
      try {
        const me = await api.me();
        setEmail(me.email);
        setPlayerId(me.playerId);
        setIsAdmin(me.isAdmin);
        setAuthNotice(null);
        // Unify browser events with the server's distinctId (email).
        identifyUser(me.email);
        if (me.needsOnboarding) {
          setSnapshot(null);
          setAuth("needs_onboarding");
          return;
        }
        await refresh();
        setAuth("ready");
        return;
      } catch (e) {
        const status = (e as { status?: number }).status;
        const justExchanged = Date.now() - lastExchangeAt.current < 10_000;
        if ((status !== 401 || justExchanged) && attempt < 3) {
          await new Promise((r) => setTimeout(r, 750 * 2 ** attempt));
          continue;
        }
        if (status === 401 && justExchanged) {
          setAuthNotice(
            "Your sign-in worked but isn't sticking on this device. If you're using Bloom as an installed app, delete it from your home screen and re-add it from Safari, then try again.",
          );
        }
        setAuth("signed_out");
        setEmail(null);
        setPlayerId(null);
        setIsAdmin(false);
        setSnapshot(null);
        return;
      }
    }
  }, [refresh]);

  useEffect(() => {
    void (async () => {
      // Invite links land here with ?invite=<email>: they open the app with that email
      // prefilled on the sign-in screen (the parent then requests a 6-digit code). Auth is
      // OTP-only — there is no ?token= link to exchange.
      const url = new URL(window.location.href);
      const invite = url.searchParams.get("invite");
      if (invite) {
        // Start a FRESH sign-in for the invited email. Drop any session already on this
        // device first — on a shared family device the app would otherwise silently resume
        // the existing account and never show the login screen.
        try {
          await api.logout();
        } catch {
          /* wasn't signed in — fine */
        }
        setPrefillEmail(invite);
        window.history.replaceState({}, "", window.location.pathname);
      }
      await refreshAuth();
    })();
  }, [refreshAuth]);

  const signInWithCode = useCallback(
    async (email2: string, code: string) => {
      await api.exchangeCode(email2, code);
      lastExchangeAt.current = Date.now();
      await refreshAuth();
    },
    [refreshAuth],
  );

  const previewSignIn = useCallback(async () => {
    await api.previewLogin();
    lastExchangeAt.current = Date.now();
    await refreshAuth();
  }, [refreshAuth]);

  const createPlayer = useCallback(
    async (childName: string, character: string) => {
      await api.createPlayer(childName, character);
      await refreshAuth();
    },
    [refreshAuth],
  );

  const renamePlayer = useCallback(
    async (childName: string) => {
      await api.renamePlayer(childName);
      await refresh();
    },
    [refresh],
  );

  const changeCharacter = useCallback(
    async (character: string) => {
      await api.changeCharacter(character);
      await refresh();
    },
    [refresh],
  );

  const completeLesson = useCallback(
    async (nodeId: string, score: number) => {
      const result = await api.completeLesson(nodeId, score);
      await refresh();
      return result;
    },
    [refresh],
  );

  const replayTour = useCallback(() => setTourNonce((n) => n + 1), []);

  const logout = useCallback(async () => {
    await api.logout();
    resetAnalytics();
    setAuth("signed_out");
    setEmail(null);
    setPlayerId(null);
    setIsAdmin(false);
    setSnapshot(null);
  }, []);

  const value = useMemo<Store>(
    () => ({
      auth,
      email,
      playerId,
      isAdmin,
      snapshot,
      authNotice,
      prefillEmail,
      tourNonce,
      replayTour,
      refresh,
      refreshAuth,
      signInWithCode,
      previewSignIn,
      createPlayer,
      renamePlayer,
      changeCharacter,
      completeLesson,
      logout,
    }),
    [auth, email, playerId, isAdmin, snapshot, authNotice, prefillEmail, tourNonce, replayTour, refresh, refreshAuth, signInWithCode, previewSignIn, createPlayer, renamePlayer, changeCharacter, completeLesson, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStore(): Store {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
}
